import React from "react";
import ReactiveComponent from "@usereactify/search-internals/lib/components/basic/ReactiveComponent";
import MultiList from "@usereactify/search-internals/lib/components/list/MultiList";
import SingleList from "@usereactify/search-internals/lib/components/list/SingleList";
import SingleRange from "@usereactify/search-internals/lib/components/range/SingleRange";
import MultiRange from "@usereactify/search-internals/lib/components/range/MultiRange";

import type {
  ConfigFilterFacet,
  ReactivesearchFilterListProps,
} from "../../types";
import {
  useFilterListProps,
  useFilterRangeProps,
  useFilterSliderProps,
  useFilters,
  useReactifySearchContext,
} from "../../hooks";
import {
  ExampleFilterList,
  ExampleFilterRange,
  ExampleFilterSlider,
} from "../../components";
import { getPropsFilterList, getPropsFilterRange, getPropsShared } from "../../utility/props";

export type FilterProps = {
  /** The filter option being rendered */
  filter: ConfigFilterFacet;
  /** Render method for List filters */
  renderFilterList?: React.FC<{
    filter: ConfigFilterFacet;
    filterListProps: ReturnType<typeof useFilterListProps>;
  }>;
  /** Render method for Range filters */
  renderFilterRange?: React.FC<{
    filter: ConfigFilterFacet;
    filterRangeProps: ReturnType<typeof useFilterRangeProps>;
  }>;
  /** Render method for Slider filters */
  renderFilterSlider?: React.FC<{
    filter: ConfigFilterFacet;
    filterSliderProps: ReturnType<typeof useFilterSliderProps>;
    // reactSliderProps: ReturnType<typeof useReactSliderProps>;
  }>;
  /** Show filters even when no options are available */
  showEmptyFilters?: boolean;
};

export const Filter: React.FC<FilterProps> = (props) => {
  if ("single" === props.filter.displayType) {
    if ("range" === props.filter.displayView) {
      return <FilterRangeSingle {...props} />;
    }
    return <FilterSingleList {...props} />;
  }

  if ("multi" === props.filter.displayType) {
    if ("range" === props.filter.displayView) {
      return <FilterRangeMulti {...props} />;
    }
    return <FilterMultiList {...props} />;
  }

  if ("slider" === props.filter.displayType) {
    return <FilterSlider {...props} />;
  }

  // TODO: Legacy range filter, migrate to new single and multi ranges
  if ("range" === props.filter.displayType) {
    return <FilterRangeSingle {...props} />;
  }

  console.warn(
    `filter with display type "${props.filter.displayType}" not yet supported`
  );

  return null;
};

const FilterSingleList: React.FC<FilterProps> = (props) => {
  const reactifySearchContext = useReactifySearchContext();
  const filtersHook = useFilters();
  const sharedProps = getPropsShared({
    provider: reactifySearchContext.options,
    config: reactifySearchContext.config,
    filterGroup: filtersHook.filterStack,
  }, props.filter);
  const reactiveProps = getPropsFilterList(props.filter);

  return (
    <SingleList
      {...sharedProps}
      {...reactiveProps}
      render={(reactivesearchFilterProps: ReactivesearchFilterListProps) => (
        <FilterListInner
          {...props}
          reactivesearchFilterProps={reactivesearchFilterProps}
        />
      )}
    />
  );
};

const FilterMultiList: React.FC<FilterProps> = (props) => {
  const reactifySearchContext = useReactifySearchContext();
  const filtersHook = useFilters();
  const sharedProps = getPropsShared({
    provider: reactifySearchContext.options,
    config: reactifySearchContext.config,
    filterGroup: filtersHook.filterStack,
  }, props.filter);
  const reactiveProps = getPropsFilterList(props.filter);

  return (
    <MultiList
      {...sharedProps}
      {...reactiveProps}
      render={(reactivesearchFilterProps: ReactivesearchFilterListProps) => (
        <FilterListInner
          {...props}
          reactivesearchFilterProps={reactivesearchFilterProps}
        />
      )}
    />
  );
};

const FilterRangeSingle: React.FC<FilterProps> = (props) => {
  const reactifySearchContext = useReactifySearchContext();
  const filtersHook = useFilters();
  const sharedProps = getPropsShared({
    provider: reactifySearchContext.options,
    config: reactifySearchContext.config,
    filterGroup: filtersHook.filterStack,
  }, props.filter);
  const reactiveProps = getPropsFilterRange(props.filter);

  const filterRangeProps = useFilterRangeProps(props.filter);

  const RenderRangeComponent = props.renderFilterRange ?? ExampleFilterRange;

  return (
    <>
      <div
        style={{
          display: "none",
        }}
      >
        <SingleRange
          {...sharedProps}
          {...reactiveProps}
          value={filterRangeProps.values[0]}
          onChange={filterRangeProps.handleChange}
        />
      </div>
      <RenderRangeComponent
        filterRangeProps={filterRangeProps}
        filter={props.filter}
      />
    </>
  );
};

const FilterRangeMulti: React.FC<FilterProps> = (props) => {
  const reactifySearchContext = useReactifySearchContext();
  const filtersHook = useFilters();
  const sharedProps = getPropsShared({
    provider: reactifySearchContext.options,
    config: reactifySearchContext.config,
    filterGroup: filtersHook.filterStack,
  }, props.filter);
  const reactiveProps = getPropsFilterRange(props.filter);

  const filterRangeProps = useFilterRangeProps(props.filter);

  const RenderRangeComponent = props.renderFilterRange ?? ExampleFilterRange;

  return (
    <>
      <div
        style={{
          display: "none",
        }}
      >
        <MultiRange
          {...sharedProps}
          {...reactiveProps}
          value={filterRangeProps.values}
          // @note: onChange handler must be defined for controlled component to work correctly
          onChange={() => {}}
          onValueChange={(values) => {
            if (values.length === 0) {
              filterRangeProps.handleClear();
            }
          }}
        />
      </div>
      <RenderRangeComponent
        filterRangeProps={filterRangeProps}
        filter={props.filter}
      />
    </>
  );
};

const FilterSlider: React.FC<FilterProps> = (props) => {
  const reactifySearchContext = useReactifySearchContext();
  const filtersHook = useFilters();
  const sharedProps = getPropsShared({
    provider: reactifySearchContext.options,
    config: reactifySearchContext.config,
    filterGroup: filtersHook.filterStack,
  }, props.filter);

  return (
    <ReactiveComponent
      {...sharedProps}
      defaultQuery={() => ({
        aggs: {
          min: {
            min: {
              field: props.filter.field,
            },
          },
          max: {
            max: {
              field: props.filter.field,
            },
          },
        },
      })}
      render={(reactivesearchFilterProps) => {
        if (
          !props.showEmptyFilters &&
          reactivesearchFilterProps?.aggregations?.min?.value ===
          reactivesearchFilterProps?.aggregations?.max?.value
        ) {
          return null;
        }

        return (
          <FilterSliderInner
            {...props}
            reactivesearchFilterProps={reactivesearchFilterProps}
          />
        );
      }}
    />
  );
};

// inner component exists only so we can use memos in reactivesearch render functions
const FilterSliderInner: React.FC<
  FilterProps & { reactivesearchFilterProps: any }
> = (props) => {
  const {
    filter,
    renderFilterSlider,
    reactivesearchFilterProps,
    ...otherProps
  } = props;

  const filterSliderProps = useFilterSliderProps(filter);

  React.useEffect(() => {
    if (reactivesearchFilterProps.aggregations) {
      filterSliderProps.handleRange([
        reactivesearchFilterProps.aggregations.min.value,
        reactivesearchFilterProps.aggregations.max.value,
      ]);
    }
  }, [
    reactivesearchFilterProps.aggregations?.min.value,
    reactivesearchFilterProps.aggregations?.max.value,
  ]);

  React.useEffect(() => {
    if (filterSliderProps.value[0] === 0 && filterSliderProps.value[1] === 0) {
      return;
    }

    props.reactivesearchFilterProps.setQuery({
      query: {
        query: {
          bool: {
            must: [
              {
                bool: {
                  must: [
                    {
                      match: {
                        published: true,
                      },
                    },
                    {
                      range: {
                        [props.filter.field]: {
                          gte: filterSliderProps.value[0],
                          lte: filterSliderProps.value[1],
                          boost: 2,
                        },
                      },
                    },
                  ],
                },
              },
            ],
          },
        },
      },
      value: filterSliderProps.value,
    });
  }, [filterSliderProps.value]);

  const RenderListComponent = props.renderFilterSlider ?? ExampleFilterSlider;

  return (
    <RenderListComponent
      filterSliderProps={filterSliderProps}
      filter={filter}
      {...otherProps}
    />
  );
};

// inner component exists only so we can use memos in reactivesearch render functions
const FilterListInner: React.FC<
  FilterProps & { reactivesearchFilterProps: ReactivesearchFilterListProps }
> = (props) => {
  const { filter, renderFilterList, reactivesearchFilterProps, ...otherProps } =
    props;

  const filterListProps = useFilterListProps(filter, reactivesearchFilterProps);

  // hide entire filter when no options available
  if (!props.showEmptyFilters && 0 === filterListProps.options.length) {
    return null;
  }

  const RenderListComponent = props.renderFilterList ?? ExampleFilterList;

  return (
    <RenderListComponent
      filterListProps={filterListProps}
      filter={filter}
      {...otherProps}
    />
  );
};
